import { ChangeEvent, useState } from 'react';

import { cn } from '~/lib/utils';

import { UploadIcon } from '@radix-ui/react-icons';

import { SpinnerIcon } from '../../assets/icons';
import { iconButtonVariants } from '~/components/ui/IconButton';

export interface FileUploaderProps {
  config?: {
    inputId?: string;
    fileSizeLimit?: number;
    allowedExtensions?: string[];
    checkExtension?: (extension: string) => string | null;
    disabled: boolean;
  };
  onFileUpload: (file: File) => Promise<void>;
  onFileError?: (errMsg: string) => void;
}

const DEFAULT_INPUT_ID = 'fileInput';
const DEFAULT_FILE_SIZE_LIMIT = 1024 * 1024 * 5; // 5 MB

export default function FileUploader({ config, onFileUpload, onFileError }: FileUploaderProps) {
  const [uploading, setUploading] = useState(false);

  const inputId = config?.inputId || DEFAULT_INPUT_ID;
  const fileSizeLimit = config?.fileSizeLimit || DEFAULT_FILE_SIZE_LIMIT;
  const allowedExtensions = config?.allowedExtensions || ['jpg', 'jpeg', 'png'];
  const defaultCheckExtension = (extension: string) => {
    if (allowedExtensions && !allowedExtensions.includes(extension.toLowerCase())) {
      return `Invalid file type. Please select a file with one of these formats: ${allowedExtensions.join(',')}`;
    }
    return null;
  };
  const checkExtension = config?.checkExtension ?? defaultCheckExtension;

  const isFileSizeExceeded = (file: File) => {
    return file.size > fileSizeLimit;
  };

  const resetInput = () => {
    const fileInput = document.querySelector(`#${inputId}`) as HTMLInputElement;
    fileInput.value = '';
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    await handleUpload(file);
    resetInput();
    setUploading(false);
  };

  const handleUpload = async (file: File) => {
    const onFileUploadError = onFileError || window.alert;
    const fileExtension = file.name.split('.').pop() || '';
    const extensionFileError = checkExtension(fileExtension);
    if (extensionFileError) {
      return onFileUploadError(extensionFileError);
    }

    if (isFileSizeExceeded(file)) {
      return onFileUploadError(`File size exceeded. Limit is ${fileSizeLimit / 1024 / 1024} MB`);
    }

    await onFileUpload(file);
  };

  return (
    <>
      <input
        type="file"
        id={inputId}
        style={{ display: 'none' }}
        onChange={onFileChange}
        accept={allowedExtensions?.map((extension) => `${extension},${extension.toUpperCase()}`).join(',')}
        disabled={config?.disabled || uploading}
        className="FileUpload"
      />
      <label
        htmlFor={inputId}
        className={cn(
          iconButtonVariants({ variant: 'secondary' }),
          'grid shrink-0 place-items-center',
          '[.FileUpload:disabled+&]:bg-disabled-button [.FileUpload:disabled+&]:text-disabled-button-foreground  [.FileUpload:disabled+&]:border-disabled-button-border [.FileUpload:disabled+&]:translate-y-0 [.FileUpload:disabled+&]:cursor-not-allowed',
        )}
      >
        {uploading ? <SpinnerIcon /> : <UploadIcon className="h-4 w-4" />}
      </label>
    </>
  );
}
